import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import FloatingScene from "@/components/FloatingScene";
import UpsellBanner from "@/components/UpsellBanner";
import VacationBadge from "@/components/VacationBadge";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart } from "recharts";
import {
  Mail, Inbox, Users2, UserCheck, ShieldAlert, Ticket, Bot, TrendingUp, AlertOctagon, Activity, Zap,
} from "lucide-react";

const KPIs = [
  { key: "email_accounts", label: "Email accounts", icon: Mail, tone: "emerald" },
  { key: "inbox_unread", label: "Inbox unread", icon: Inbox, tone: "sun" },
  { key: "open_conversations", label: "Open convos", icon: Activity, tone: "emerald" },
  { key: "pending_approvals", label: "Awaiting approval", icon: UserCheck, tone: "sun" },
  { key: "open_tickets", label: "Open tickets", icon: Ticket, tone: "emerald" },
  { key: "critical_noc_tickets", label: "Critical NOC", icon: AlertOctagon, tone: "crimson" },
  { key: "fraud_open", label: "Fraud open", icon: ShieldAlert, tone: "crimson" },
  { key: "ai_handled", label: "AI handled", icon: Bot, tone: "sun" },
];

const toneMap = {
  emerald: { icon: "text-emerald-brand", bg: "bg-emerald-brand/12" },
  sun: { icon: "text-yellow-600", bg: "bg-sun/25" },
  crimson: { icon: "text-crimson", bg: "bg-crimson/12" },
};

function useCountUp(value, duration = 800) {
  const [v, setV] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setV(Math.round(value * (0.2 + 0.8 * p * p * (3 - 2 * p)))); // ease
      if (p < 1) raf = requestAnimationFrame(tick);
      else setV(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return v;
}

function KPICard({ k, index, value, loading }) {
  const tone = toneMap[k.tone];
  const counted = useCountUp(value || 0);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.04 }}
      className="card-3d p-5 relative overflow-hidden"
      data-testid={`kpi-${k.key}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{k.label}</div>
          <div className="mt-3 font-display text-4xl font-semibold tracking-tight">
            {loading ? <span className="inline-block h-9 w-14 ai-shimmer rounded-md" /> : counted}
          </div>
        </div>
        <div className={`h-10 w-10 rounded-xl grid place-items-center ${tone.bg} ${tone.icon}`}>
          <k.icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
      </div>
      {/* subtle line */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.6, delay: 0.25 + index * 0.04 }}
        style={{ transformOrigin: "left" }}
        className="mt-4 h-0.5 bg-gradient-to-r from-emerald-brand via-sun to-transparent rounded-full"
      />
    </motion.div>
  );
}

export default function Dashboard() {
  const { activeCompanyId, user } = useAuth();
  const [kpis, setKpis] = useState({});
  const [volume, setVolume] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = activeCompanyId ? { company_id: activeCompanyId } : {};
      try {
        const [k, v] = await Promise.all([
          api.get("/dashboard/kpis", { params }),
          api.get("/dashboard/volume", { params }),
        ]);
        setKpis(k.data);
        setVolume(v.data);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [activeCompanyId]);

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="dashboard-page">
      <div className="relative overflow-hidden border-b border-border">
        <FloatingScene dense />
        <div className="relative z-10 p-6 lg:p-10">
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-brand">Operations</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-2">
            Welcome back, <span className="italic font-serif">{user?.full_name || user?.username}</span>.
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Live view of inbox pressure, AI activity, SLA and quota across your workspaces.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 pill bg-white/70 border border-border shadow-sm">
            <span className="relative inline-block h-2 w-2 rounded-full bg-emerald-brand live-dot" />
            <span className="text-xs font-mono">Real-time sync active · every 5 min</span>
          </div>
        </div>
      </div>

      <div className="p-6 lg:p-10 space-y-6">
        <VacationBadge companyId={activeCompanyId} />
        <UpsellBanner />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {KPIs.map((k, i) => (
            <KPICard key={k.key} k={k} index={i} value={kpis[k.key] ?? 0} loading={loading} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }} className="card-3d p-6 lg:col-span-2" data-testid="chart-volume">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Last 14 days</div>
                <div className="font-display text-xl font-semibold tracking-tight mt-0.5">Email volume</div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-brand" /> Received</span>
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-sun" /> Sent</span>
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={volume}>
                  <defs>
                    <linearGradient id="gRec" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gSent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} />
                  <Area type="monotone" dataKey="received" stroke="hsl(var(--primary))" fill="url(#gRec)" strokeWidth={2} />
                  <Area type="monotone" dataKey="sent" stroke="hsl(var(--accent))" fill="url(#gSent)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }} className="card-3d p-6" data-testid="chart-summary">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5 text-sun" /> Snapshot
            </div>
            <SummaryRow label="Total sent" value={kpis.total_sent ?? 0} />
            <SummaryRow label="AI handled" value={kpis.ai_handled ?? 0} tone="emerald" />
            <SummaryRow label="Failed accounts" value={kpis.failed_accounts ?? 0} tone="crimson" />
            <SummaryRow label="Billing open" value={kpis.billing_open ?? 0} />
            <SummaryRow label="Fraud open" value={kpis.fraud_open ?? 0} tone="crimson" />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, tone }) {
  const cls = tone === "emerald" ? "text-emerald-brand" : tone === "crimson" ? "text-crimson" : "text-foreground";
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-b-0">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={`font-mono text-sm font-semibold ${cls}`}>{value}</div>
    </div>
  );
}
